// `InvoicesList` — paginated table of Stripe invoices (US-198).
//
// Renders status pill (Payée / En attente / Échouée / Remboursée /
// Brouillon), amount, date, and a per-row download button that opens the
// PDF via the API proxy (`GET /billing/invoices/:id/pdf`).
//
// "Tout télécharger" downloads each row sequentially via the same proxy
// route — bundling into a ZIP is left as a Wave 12 enhancement.

import { useT } from '@barel/i18n';
import { Button, EmptyState, Pill, type PillTone } from '@barel/ui';
import { Download, FileText } from 'lucide-react';

import { type InvoiceRow, useInvoices } from '@barel/api-client/react';

import { formatDate as formatDateIntl, formatMoney } from '@/lib/format';

const STATUS_TONE: Record<InvoiceRow['status'], PillTone> = {
  paid: 'success',
  open: 'warning',
  void: 'neutral',
  uncollectible: 'error',
  draft: 'neutral',
};

export interface InvoicesListProps {
  limit?: number;
  /** Override fetched data — used by stories + tests. */
  itemsOverride?: InvoiceRow[];
}

export function InvoicesList({ limit, itemsOverride }: InvoicesListProps) {
  const t = useT('billing');
  const query = useInvoices({ limit });
  const items = itemsOverride ?? query.data?.items ?? [];

  if (query.isError) {
    return (
      <EmptyState
        tone="error"
        title={t('invoices.empty')}
        description={t('invoices.emptyHint')}
        media={<FileText size={20} />}
      />
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        tone="neutral"
        title={t('invoices.empty')}
        description={t('invoices.emptyHint')}
        media={<FileText size={20} />}
        data-testid="invoices-empty"
      />
    );
  }

  return (
    <div
      data-component="InvoicesList"
      className="overflow-hidden rounded-lg border border-default bg-bg-surface"
    >
      <div className="flex items-center justify-between border-b border-subtle bg-bg-elevated px-4 py-2">
        <div className="text-2xs font-semibold uppercase tracking-wider text-tertiary">
          {t('invoices.subtitle')}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => downloadAll(items)}
          data-testid="invoices-download-all"
        >
          {t('invoices.downloadAll')}
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-bg-elevated text-2xs uppercase tracking-wider text-tertiary">
          <tr>
            <th scope="col" className="px-4 py-2 text-left">
              {t('invoices.date')}
            </th>
            <th scope="col" className="px-4 py-2 text-left">
              {t('invoices.number')}
            </th>
            <th scope="col" className="px-4 py-2 text-right">
              {t('invoices.amount')}
            </th>
            <th scope="col" className="px-4 py-2 text-left">
              {t('invoices.status')}
            </th>
            <th scope="col" className="px-4 py-2 text-right">
              <span className="sr-only">{t('invoices.downloadOne', { number: '' })}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((inv) => (
            <tr key={inv.id} className="border-t border-subtle" data-testid="invoice-row">
              <td className="whitespace-nowrap px-4 py-2 tabular-nums text-secondary">
                {formatDate(inv.createdAt)}
              </td>
              <td className="px-4 py-2 font-medium text-primary">{inv.number}</td>
              <td className="px-4 py-2 text-right tabular-nums text-primary">
                {formatAmount(inv.amountCents, inv.currency)}
              </td>
              <td className="px-4 py-2">
                <Pill tone={STATUS_TONE[inv.status]} variant="soft" size="sm">
                  {t(`invoices.status${capitalize(inv.status)}`)}
                </Pill>
              </td>
              <td className="px-4 py-2 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadInvoice(inv)}
                  aria-label={t('invoices.downloadOne', { number: inv.number })}
                >
                  <Download size={14} />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDate(ts: number): string {
  return formatDateIntl(Number(ts), { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAmount(cents: number, currency: string): string {
  const value = cents / 100;
  try {
    return formatMoney(value, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    });
  } catch {
    return `${value.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function capitalize<T extends string>(s: T): Capitalize<T> {
  return (s.charAt(0).toUpperCase() + s.slice(1)) as unknown as Capitalize<T>;
}

function downloadInvoice(inv: InvoiceRow) {
  const url = `/api/v1/billing/invoices/${encodeURIComponent(inv.id)}/pdf`;
  // Open in a new tab — the Worker proxies the PDF with
  // `content-disposition: attachment`.
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function downloadAll(items: InvoiceRow[]) {
  for (const inv of items) {
    downloadInvoice(inv);
    // tiny stagger to let the browser handle each download.
    await new Promise((r) => setTimeout(r, 250));
  }
}
